import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import { Box, SvgIconProps } from '@mui/material';
import { useFormikContext } from 'formik';
import { LanguageSwitcher } from 'components/language-switcher';
import { Text } from 'components/Text';
import { IMAGES } from 'config/images';
import { AppLocale, getTrans, TranslationKey } from 'modules/i18n';
import { CVBuilderFormKeys, CVBuilderFormValues } from 'utils/cv-form-db';
import { StepNavigationButton } from '../../components/StepNavigationButton';
import {
  HeaderContainer,
  LogoBox,
  RootBox,
  TitleContainer
} from '../../components/styledComponents';
import { ZENJOB_LOGO_ALT_TEXT } from '../../constants';
import { useCvBuilderData } from '../../data/CvBuilderDataContext';

/**
 * What the talent gets out of finishing, one line each.
 *
 * This step used to open with six stacked paragraphs plus a five-fragment info card before
 * the first tap — the process details (partner-company review, GDPR, "we'll call you") now
 * live where they answer a live question: the post-submit screen. Not one of the intro
 * screens we benchmarked uses prose here; they all use a headline plus three or four
 * icon + one-line rows.
 */
const BENEFITS: {
  Icon: React.ComponentType<SvgIconProps>;
  key: TranslationKey;
}[] = [
  { Icon: WorkOutlineOutlinedIcon, key: 'cvBuilderPage.intro.benefit1' },
  { Icon: VisibilityOutlinedIcon, key: 'cvBuilderPage.intro.benefit2' },
  { Icon: EditNoteOutlinedIcon, key: 'cvBuilderPage.intro.benefit3' }
];

export const LanguageStep = () => {
  const { setFieldValue } = useFormikContext<CVBuilderFormValues>();
  const { clearDraft } = useCvBuilderData();

  const handleLanguageSelect = async (locale: AppLocale) => {
    // Authenticated: resets the local IndexedDB draft. Guest: no-op (server-owned),
    // so changing language never wipes a resumed CV.
    await clearDraft();
    await setFieldValue(CVBuilderFormKeys.LANGUAGE, locale);
  };

  return (
    <RootBox
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <HeaderContainer sx={{ p: 0 }}>
        <LogoBox>
          <img alt={ZENJOB_LOGO_ALT_TEXT} src={IMAGES.proLogoPurple} />
        </LogoBox>
        <LanguageSwitcher onSelect={handleLanguageSelect} />
      </HeaderContainer>

      <TitleContainer sx={{ pt: 5, pb: 2 }}>
        <Text variant="h2" color="text.primary">
          {getTrans('cvBuilderPage.intro.heading')}
        </Text>
        <Text variant="body2" sx={{ mt: 1 }}>
          {getTrans('cvBuilderPage.intro.subheading')}
        </Text>
      </TitleContainer>

      <Box sx={{ px: 2, pb: 2 }}>
        {BENEFITS.map(({ Icon, key }) => (
          <Box
            key={key}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}
          >
            <Icon
              sx={theme => ({
                flex: 'none',
                fontSize: 22,
                mt: '2px',
                color: theme.palette.primary.main
              })}
            />
            <Text variant="body2" sx={{ minWidth: 0 }}>
              {getTrans(key)}
            </Text>
          </Box>
        ))}
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      <StepNavigationButton step="LANGUAGE">
        {getTrans('cvBuilderPage.intro.button')}
      </StepNavigationButton>

      {/* Price the work up front: how many steps, roughly how long, and that stopping
          halfway is safe. All three are facts the flow already guarantees. */}
      <Text
        variant="caption"
        sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}
      >
        {getTrans('cvBuilderPage.intro.effort')}
      </Text>

      {/* The language choice is a real decision (the CV is written in it and read by the
          partner company), but it belongs next to the switcher as a hint — not as a
          five-fragment info card competing with the CTA. */}
      <Text
        variant="caption"
        sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}
      >
        {getTrans('cvBuilderPage.intro.languageHint')}
      </Text>

      {/* Kept verbatim from the retired intro copy: this is a privacy disclosure, so it
          moves out of the paragraph wall rather than disappearing with it. */}
      <Text
        variant="caption"
        sx={{ display: 'block', textAlign: 'center', mt: 1.5, mb: 2 }}
      >
        {getTrans('cvBuilderPage.intro.privacyNote')}
      </Text>
    </RootBox>
  );
};
